<script setup lang="ts">
import { formatCurrency } from 'src/utils/currency';
import { getMeasurementShort } from 'src/utils/article-utils';
import type { FetchArticleResponseLite } from 'src/api/models/fetchArticleResponseLite';

interface Props {
  articles: FetchArticleResponseLite[];
  loading: boolean;
  page: number;
  totalPages: number;
  totalRecords: number;
  selectedArticleId: string | null;
  compact?: boolean;
}

const { articles, loading, page, totalPages, totalRecords, selectedArticleId, compact = false } = defineProps<Props>();

const emit = defineEmits<{
  'row-click': [article: FetchArticleResponseLite];
  'page-change': [page: number];
}>();

function onRowClick (event: { data: FetchArticleResponseLite }) {
  emit('row-click', event.data);
}

function onPageChange (event: { page: number }) {
  emit('page-change', event.page + 1);
}

</script>

<template>
  <DataTable
    data-testid="article-list-table"
    :value="articles"
    :loading="loading"
    selection-mode="single"
    :row-class="(data: FetchArticleResponseLite) => data.id === selectedArticleId ? 'bg-primary/5' : ''"
    scrollable
    scroll-height="flex"
    class="h-full"
    @row-click="onRowClick"
  >
    <template #empty>
      <div data-testid="article-list-empty" class="text-center py-8 text-color-secondary">
        No articles found
      </div>
    </template>

    <template #loading>
      <div class="flex items-center gap-2 py-8 justify-center">
        <i class="pi pi-spin pi-spinner" />
        Loading articles...
      </div>
    </template>

    <Column field="name" header="Name" :sortable="true" style="min-width: 100px">
      <template #body="{ data }">
        <div data-testid="article-name" class="flex flex-col">
          <span class="font-medium">{{ data.name }}</span>
          <span v-if="data.preferred_article_option?.brand_name" class="text-xs text-color-secondary">{{ data.preferred_article_option.brand_name }}</span>
        </div>
      </template>
    </Column>

    <Column field="reference" header="Reference" :sortable="true" style="min-width: 80px">
      <template #body="{ data }">
        <div class="flex flex-col">
          <span data-testid="article-reference" class="font-mono text-xs">{{ data.preferred_article_option?.external_reference || '-' }}</span>
        </div>
      </template>
    </Column>

    <Column field="sale_price" header="Price" :sortable="true" style="min-width: 90px">
      <template #body="{ data }">
        <span data-testid="article-sales-price" class="font-mono text-sm">
          <template v-if="data.sale_price != null">{{ formatCurrency(data.sale_price) }}<span v-if="data.measurement" class="text-xs text-color-secondary">/{{ getMeasurementShort(data.measurement) }}</span></template>
          <template v-else>-</template>
        </span>
      </template>
    </Column>
  </DataTable>

  <Paginator
    v-if="totalPages > 1"
    data-testid="article-paginator"
    :rows="25"
    :total-records="totalRecords"
    :first="(page - 1) * 25"
    @page="onPageChange"
  />
</template>
